import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TextField, Button, FormControlLabel, Checkbox } from "@mui/material";
import "./auth.css";
import { api, setAuth } from "../api"; // <-- use centralized API client
import usePageTitle from "../hooks/usePageTitle";

const Login = () => {
  usePageTitle("Login");
  const [formData, setFormData] = useState({
    identifier: "", // username or email
    password: "",
    rememberMe: true,
  });
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    if (!formData.identifier.trim()) {
      setMessage("Please enter your username or email.");
      return;
    }
    if (!formData.password) {
      setMessage("Please enter your password.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        username: formData.identifier.trim(), // backend accepts username (can be email string)
        password: formData.password,
        rememberMe: formData.rememberMe,
      };

      // Use /api/login as per Firebase rewrites (relative URL)
      const res = await api.post('/api/login', payload, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      const { user } = res.data || {};
      if (!user) {
        setMessage("Unexpected response from server.");
        return;
      }

      let finalUser = user;
      try {
        const fresh = await api.get(`/api/users/${user.id}`);
        finalUser = fresh?.data?.user || fresh?.data || user;
      } catch {
        finalUser = user;
      }

      // persist via shared helper (cookie carries the session; frontend only stores user profile)
      setAuth({ user: finalUser, remember: formData.rememberMe });

      setMessage("✅ Login successful!");

      const usernameForRoute = encodeURIComponent(finalUser.name || finalUser.username || formData.identifier.trim());
      setTimeout(() => navigate(`/mycollection/${usernameForRoute}`), 400);
    } catch (err) {
      const msg = err?.response?.data?.error || "❌ Invalid credentials.";
      setMessage(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-sidebar">
          <h3>Welcome Back!</h3>
          <p>Log in to access your collection.</p>
        </div>

        <div className="auth-form">
          <h2>Login</h2>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Username or Email"
              fullWidth
              size="small"
              name="identifier"
              value={formData.identifier}
              onChange={handleChange}
              autoComplete="username"
              required
              sx={{ mb: 0.9 }}
            />
            <TextField
              label="Password"
              fullWidth
              size="small"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              autoComplete="current-password"
              required
              sx={{ mb: 0.6 }}
            />
            <FormControlLabel
              sx={{ my: 0.15 }}
              control={
                <Checkbox
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  name="rememberMe"
                />
              }
              label="Remember Me"
            />
            <Button type="submit" variant="contained" fullWidth className="auth-button" disabled={submitting} sx={{ mt: 0.5 }}>
              {submitting ? "Signing in…" : "LOGIN"}
            </Button>
          </form>

          {message && <p className="message">{message}</p>}

          <p style={{ marginTop: 8 }}>
            <Link to="/forgot-password">Forgot Password?</Link>
          </p>
          <p style={{ marginTop: 8 }}>
            Don't have an account? <Link to="/register">Sign Up</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
